import { createContext, useContext, useEffect, useMemo, useState } from "react";
import usedLanguage from "../services/language/usedLanguage";
import type { ReactNode } from "react";
import { getLanguageFromUrl, LANGUAGE_PARAM, withCurrentLanguage } from "@utils/getLanguage";

interface LanguageContextValue {
  language: string;
  setLanguage: (language: string) => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<string>(usedLanguage(getLanguageFromUrl()));

  useEffect(() => {
    const handleLocationChange = () => setLanguageState(getLanguageFromUrl());

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (state, unused, url) {
      return originalPushState.call(this, state, unused, withCurrentLanguage(url));
    };

    window.history.replaceState = function (state, unused, url) {
      return originalReplaceState.call(this, state, unused, withCurrentLanguage(url));
    };

    const handleLinkClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;

      const link = target.closest("a");
      if (!link || link.target === "_blank" || link.hasAttribute("download")) return;

      const href = link.getAttribute("href");
      const updatedHref = withCurrentLanguage(href);
      if (typeof updatedHref === "string" && updatedHref !== href) {
        link.setAttribute("href", updatedHref);
      }
    };

    window.addEventListener("popstate", handleLocationChange);
    document.addEventListener("click", handleLinkClick, true);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", handleLocationChange);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, []);

  const setLanguage = (newLanguage: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set(LANGUAGE_PARAM, newLanguage);
    window.history.pushState({}, "", url);
    setLanguageState(usedLanguage(newLanguage));
  };

  const value = useMemo(() => ({ language, setLanguage }), [language]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
